import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslatedString } from 'lemunzo-api';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { PlatformZones } from '../../enums/client-admin-zone';
import { CurrentUser } from '../../models/current-user';
import { LanguageCode } from '../../models/language-code';
import { LocalizationService } from '../../services/localization.service';
import { LanguageSelectionComponent } from '../language-selection/language-selection.component';
import { NotificationsBadgeComponent } from '../notifications-badge/notifications-badge.component';
import { ProfileDetailComponent } from '../profile-detail/profile-detail.component';
import { TranslationComponent } from '../translation/translation.component';

@Component({
    selector: 'apcam-top-navbar',
    templateUrl: './top-navbar.component.html',
    styleUrls: ['./top-navbar.component.scss'],
    imports: [
        AsyncPipe,
        LanguageSelectionComponent,
        MatButtonModule,
        MatIconModule,
        MatToolbarModule,
        MatTooltipModule,
        ProfileDetailComponent,
        TranslatePipe,
        TranslationComponent,
        NotificationsBadgeComponent,
    ],
})
export class TopNavbarComponent implements OnInit {
    /**
     * Input property representing the current user information.
     */
    @Input()
    currentUser!: CurrentUser;

    /**
     * Input property determining whether to display the username or case name.
     *
     * @default false
     */
    @Input()
    showUserOrCaseName: boolean = false;

    @Input({ required: true })
    public availableLanguages!: WritableSignal<LanguageCode[]>;

    @Input({ required: true })
    notificationCount: WritableSignal<number> = signal(0);

    /**
     * Input property specifying the client or admin zone for the component.
     *
     * @usageNotes
     * The `zone` property is used to specify the client or admin zone within the component.
     * It is of type PlatformZones, with a default value of `PlatformZones.ADMIN_ZONE`.
     *
     * @default PlatformZones.ADMIN_ZONE
     */
    // TODO remove PlatformZone enum
    @Input()
    zone: PlatformZones = PlatformZones.ADMIN_ZONE;

    /**
     * Case name for the component.
     *
     * @usageNotes
     * The `caseName` property is used to provide the case name within the component.
     * It accepts a string, and the default value is an empty string.
     *
     * @default undefined
     */
    @Input()
    caseName: TranslatedString | undefined = undefined;

    @Output()
    openNotificationDialog = new EventEmitter();

    /**
     * Observes whether the viewport meets the XSmall breakpoint (0-600px)
     */
    breakpointSm$: Observable<boolean> = new Observable();

    currLang: string = this.localizationService.getCurrentLanguage();

    protected readonly PlatformZones = PlatformZones;

    constructor(
        private readonly breakPointObserver: BreakpointObserver,
        private readonly localizationService: LocalizationService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        // For smallest devices, decrease the padding on the toolbar to make more space for icons
        this.breakpointSm$ = this.breakPointObserver
            .observe([Breakpoints.XSmall])
            .pipe(
                distinctUntilChanged(),
                map((result) => result.matches)
            );
    }

    protected logout(): void {
        this.router.navigate(['logout']);
    }
}
